import { useState, useCallback } from 'react';
import { Select } from '@/shared/components/ui/Select';
import {
  useLocations,
  useDashboardSettings,
  useLocationManufacturerSettings,
} from '../hooks/useAdmin';
import { useMyPhoneNumber } from '@/features/settings/hooks/useNotificationPreferences';
import { DashboardSettingsCard } from './DashboardSettingsCard';
import { DashboardSettingsForm } from './DashboardSettingsForm';
import { ManufacturerOverridesTable } from './ManufacturerOverridesTable';
import { ManufacturerOverrideForm } from './ManufacturerOverrideForm';
import { NotificationContactCard } from './NotificationContactCard';
import { AlertNotificationSettings } from './AlertNotificationSettings';
import type { LocationManufacturerSettingsWithDetails } from '../services/locationManufacturerSettingsService';

interface AlertNotificationPreference {
  alertType: string;
  emailOnYellow: boolean;
  emailOnRed: boolean;
  smsOnYellow: boolean;
  smsOnRed: boolean;
}

export function PerLocationSettingsTab() {
  const { data: locations } = useLocations();
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [isDashboardFormOpen, setIsDashboardFormOpen] = useState(false);
  const [isOverrideFormOpen, setIsOverrideFormOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState<
    LocationManufacturerSettingsWithDetails | undefined
  >();
  
  // Notification preferences state (per alert type)
  const [alertPreferences, setAlertPreferences] = useState<AlertNotificationPreference[]>([
    { alertType: 'STALE_PENDING', emailOnYellow: false, emailOnRed: true, smsOnYellow: false, smsOnRed: false },
    { alertType: 'OVERDUE_ORDERED', emailOnYellow: false, emailOnRed: true, smsOnYellow: false, smsOnRed: false },
    { alertType: 'LOW_USAGE', emailOnYellow: false, emailOnRed: false, smsOnYellow: false, smsOnRed: false },
  ]);

  const { data: dashboardSettings, isLoading: dashboardLoading } =
    useDashboardSettings(selectedLocationId);
  const { data: manufacturerOverrides, isLoading: overridesLoading } =
    useLocationManufacturerSettings(selectedLocationId);
  const { data: phoneNumber } = useMyPhoneNumber();
  
  // Handle notification preference changes
  const handlePreferenceChange = useCallback((alertType: string, field: string, value: boolean) => {
    setAlertPreferences(prev => prev.map(pref => 
      pref.alertType === alertType 
        ? { ...pref, [field]: value }
        : pref
    ));
    // TODO: Save to database via API
  }, []);

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLocationId(e.target.value);
  };

  const handleEditDashboard = () => {
    setIsDashboardFormOpen(true);
  };

  const handleAddOverride = () => {
    setEditingOverride(undefined);
    setIsOverrideFormOpen(true);
  };

  const handleEditOverride = (
    override: LocationManufacturerSettingsWithDetails
  ) => {
    setEditingOverride(override);
    setIsOverrideFormOpen(true);
  };

  const handleCloseOverrideForm = () => {
    setIsOverrideFormOpen(false);
    setEditingOverride(undefined);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">
          Per-Location Settings
        </h2>
        <p className="text-sm text-gray-600">
          Configure location-specific dashboard alerts, notifications, and manufacturer overrides
        </p>
      </div>

      {/* Notification Contact Card - Always visible */}
      <div className="mb-6">
        <NotificationContactCard />
      </div>

      {/* Location Selector */}
      <div className="mb-6">
        <Select
          label="Select Location"
          value={selectedLocationId}
          onChange={handleLocationChange}
          options={[
            { value: '', label: 'Select a location' },
            ...(locations?.map((loc) => ({
              value: loc.id,
              label: loc.name,
            })) || []),
          ]}
        />
      </div>

      {selectedLocationId ? (
        <div className="space-y-6">
          {/* Dashboard Settings */}
          {dashboardLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading settings...</p>
            </div>
          ) : dashboardSettings ? (
            <>
              <DashboardSettingsCard
                settings={dashboardSettings}
                onEdit={handleEditDashboard}
              />
              
              {/* Alert Notification Settings */}
              <AlertNotificationSettings
                settings={dashboardSettings}
                locationId={selectedLocationId}
                hasPhoneNumber={!!phoneNumber}
                preferences={alertPreferences}
                onPreferenceChange={handlePreferenceChange}
              />
            </>
          ) : null}

          {/* Manufacturer Overrides */}
          <ManufacturerOverridesTable
            overrides={manufacturerOverrides || []}
            onEdit={handleEditOverride}
            onAdd={handleAddOverride}
            isLoading={overridesLoading}
          />
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            Please select a location to view and edit settings
          </p>
        </div>
      )}

      {/* Modals */}
      {dashboardSettings && (
        <DashboardSettingsForm
          isOpen={isDashboardFormOpen}
          onClose={() => setIsDashboardFormOpen(false)}
          settings={dashboardSettings}
          locationId={selectedLocationId}
        />
      )}

      <ManufacturerOverrideForm
        isOpen={isOverrideFormOpen}
        onClose={handleCloseOverrideForm}
        locationId={selectedLocationId}
        override={editingOverride}
      />
    </div>
  );
}
